import { createTheme } from '@mui/material/styles';
import {
  brand, neutral, semantic, fontFamily, typeScale,
  spacingBase, radius, shadow, motion, touchTarget, layout,
} from './tokens';

/**
 * The MUI theme is the only consumer of tokens.js, and screens are expected to
 * consume the theme rather than styling components inline. Component defaults
 * live here so a Button or Table looks right everywhere without per-page `sx`.
 *
 * `density` — 'comfortable' (default; touch-safe, for tablet use in the room)
 * or 'compact' (dense tables and the scoring grid on desktop).
 */
export const buildTheme = ({ density = 'comfortable' } = {}) => {
  const compact = density === 'compact';
  const controlMinHeight = compact ? 36 : touchTarget;

  return createTheme({
    spacing: spacingBase,

    palette: {
      mode: 'light',
      primary: {
        main: brand[500],
        dark: brand[700],
        light: brand[300],
        contrastText: neutral[0],
      },
      secondary: { main: neutral[700], contrastText: neutral[0] },
      success: { main: semantic.success.main, dark: semantic.success.dark, light: semantic.success.light },
      warning: { main: semantic.warning.main, dark: semantic.warning.dark, light: semantic.warning.light },
      error: { main: semantic.danger.main, dark: semantic.danger.dark, light: semantic.danger.light },
      info: { main: semantic.info.main, dark: semantic.info.dark, light: semantic.info.light },
      grey: neutral,
      divider: neutral[200],
      text: {
        primary: neutral[800],
        secondary: neutral[500],
        disabled: neutral[400],
      },
      background: {
        default: neutral[25],
        paper: neutral[0],
      },
      action: {
        hover: neutral[50],
        selected: brand[50],
        focus: brand[100],
      },
    },

    shape: { borderRadius: radius.md },

    typography: {
      fontFamily,
      h1: typeScale.h1,
      h2: typeScale.h2,
      h3: typeScale.h3,
      h4: typeScale.h4,
      h5: typeScale.h3,
      h6: typeScale.h4,
      subtitle1: { ...typeScale.body, fontWeight: 600 },
      subtitle2: { ...typeScale.bodySm, fontWeight: 600 },
      body1: typeScale.body,
      body2: typeScale.bodySm,
      caption: typeScale.caption,
      overline: typeScale.overline,
      button: { ...typeScale.bodySm, fontWeight: 600, textTransform: 'none' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          // Keyboard users must always be able to see where they are. The
          // previous build defined no focus styling at all.
          '*:focus-visible': {
            outline: `2px solid ${brand[500]}`,
            outlineOffset: 2,
            borderRadius: radius.sm,
          },
          body: { backgroundColor: neutral[25] },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            minHeight: controlMinHeight,
            paddingInline: compact ? 12 : 16,
            fontWeight: 600,
            transition: `background-color ${motion.fast}, border-color ${motion.fast}, box-shadow ${motion.fast}`,
          },
          // Flat fills, not the 135deg gradients the old screens each re-wrote.
          containedPrimary: {
            backgroundColor: brand[500],
            '&:hover': { backgroundColor: brand[700] },
          },
          outlined: {
            borderColor: neutral[300],
            color: neutral[700],
            '&:hover': { borderColor: neutral[400], backgroundColor: neutral[50] },
          },
          outlinedPrimary: {
            borderColor: brand[300],
            color: brand[700],
            '&:hover': { borderColor: brand[500], backgroundColor: brand[50] },
          },
          sizeSmall: { minHeight: compact ? 32 : 38 },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: { borderRadius: radius.sm },
          sizeSmall: { padding: compact ? 6 : 8 },
        },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: radius.lg,
          },
          elevation0: { border: `1px solid ${neutral[200]}` },
          elevation1: { boxShadow: shadow.sm, border: `1px solid ${neutral[200]}` },
          elevation2: { boxShadow: shadow.md },
          elevation3: { boxShadow: shadow.lg },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { border: `1px solid ${neutral[200]}`, borderRadius: radius.lg },
        },
      },

      MuiTable: {
        defaultProps: { size: compact ? 'small' : 'medium' },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            // Neutral, not a pink wash. Structure is not an action.
            '& .MuiTableCell-head': {
              backgroundColor: neutral[50],
              color: neutral[600],
              fontWeight: 600,
              fontSize: typeScale.caption.fontSize,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              borderBottom: `1px solid ${neutral[200]}`,
              whiteSpace: 'nowrap',
            },
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${neutral[100]}` },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: `background-color ${motion.fast}`,
            '&:last-child td': { borderBottom: 0 },
          },
          hover: { '&:hover': { backgroundColor: neutral[50] } },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { borderRadius: radius.xs, fontWeight: 600 },
          sizeSmall: { height: 22, fontSize: typeScale.caption.fontSize },
        },
      },

      MuiTextField: {
        defaultProps: { size: compact ? 'small' : 'medium' },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            backgroundColor: neutral[0],
            '& .MuiOutlinedInput-notchedOutline': { borderColor: neutral[300] },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: neutral[400] },
            '&.Mui-focused': { boxShadow: shadow.focus },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1, borderColor: brand[500] },
          },
          input: { minHeight: compact ? undefined : touchTarget - 18 },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.lg, boxShadow: shadow.lg },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { ...typeScale.h3, paddingBottom: 8 } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: 20, paddingTop: 8, gap: 8 } },
      },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: radius.md, border: '1px solid', alignItems: 'center' },
          standardSuccess: { borderColor: semantic.success.main + '33' },
          standardWarning: { borderColor: semantic.warning.main + '33' },
          standardError: { borderColor: semantic.danger.main + '33' },
          standardInfo: { borderColor: semantic.info.main + '33' },
        },
      },

      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: radius.md, boxShadow: shadow.lg, border: `1px solid ${neutral[200]}`, marginTop: 4 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            margin: '2px 6px',
            minHeight: controlMinHeight,
            '&.Mui-selected': { backgroundColor: brand[50], color: brand[700] },
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: neutral[800],
            borderRadius: radius.sm,
            fontSize: typeScale.caption.fontSize,
            padding: '6px 10px',
          },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backgroundColor: neutral[0],
            color: neutral[800],
            borderBottom: `1px solid ${neutral[200]}`,
          },
        },
      },

      MuiToolbar: {
        styleOverrides: { root: { minHeight: `${layout.appBarHeight}px !important` } },
      },

      MuiLink: {
        defaultProps: { underline: 'hover' },
        styleOverrides: { root: { color: brand[700], fontWeight: 500 } },
      },

      MuiDivider: {
        styleOverrides: { root: { borderColor: neutral[200] } },
      },
    },
  });
};

const theme = buildTheme();
export default theme;
